/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { redirect } from 'react-router';
import type { Route } from './+types/_app._index';
import { fetchWishlistInitialState } from '@/lib/wishlist/fetch-initial-state.server';
import type { WishlistInitialState } from '@/lib/wishlist/state';
import { WishlistProvider } from '@/providers/wishlist';
import { resolvePrefix } from '@salesforce/storefront-next-runtime/site-context';
import { Region } from '@/components/region';
import { getConfig } from '@salesforce/storefront-next-runtime/config';
import { PageType } from '@/lib/decorators/page-type';
import { fetchPageWithComponentData, type PageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { getLogger } from '@/lib/logger.server';
import hero01 from '/images/hero-01.webp';
import { SeoMeta } from '@/components/seo-meta';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { useTranslation } from 'react-i18next';
import { isDesignModeActive, isPreviewModeActive } from '@salesforce/storefront-next-runtime/design/mode';
import { usePageDesignerMode } from '@salesforce/storefront-next-runtime/design/react/core';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { resolveFallbackLanguage } from '@/lib/i18n/resolve-fallback-language';

export const HOMEPAGE_ROW_IDS = [
    'homepage-row-1',
    'homepage-row-2',
    'homepage-row-3',
    'homepage-row-4',
    'homepage-row-5',
    'homepage-row-6',
    'homepage-row-7',
] as const;

export type HomepageRowId = (typeof HOMEPAGE_ROW_IDS)[number];

export type HomePageRow = {
    id: string;
    page: PageWithComponentData | null;
};

export { shouldRevalidate } from '@/lib/revalidation/routes/home';

@PageType({
    name: 'Home Page',
    description: 'Main landing page composed of up to seven independently schedulable Page Designer rows',
    supportedAspectTypes: [],
})
export class HomePageMetadata {}

@PageType({
    name: 'Homepage Row',
    description: 'Single-region layout for one homepage row...',
    supportedAspectTypes: [],
})
@RegionDefinition([{ id: 'main', name: 'Main Region', maxComponents: 10 }])
export class HomepageRowMetadata {}

export type HomePageData = {
    rows: HomePageRow[];
    wishlistInitialState: Promise<WishlistInitialState>;
    pageUrl: string;
    ogImageUrl: string;
};

async function fetchHomepageRow(args: Route.LoaderArgs, pageId: HomepageRowId): Promise<HomePageRow> {
    try {
        const page = await fetchPageWithComponentData(args, { pageId }, { awaitComponentData: true });
        return { id: pageId, page };
    } catch (error) {
        const logger = getLogger(args.context);
        logger.warn('Homepage row fetch failed', { pageId, error });
        return { id: pageId, page: null };
    }
}

/**
 * Server-side loader function that fetches home page data.
 * This function runs on the server during SSR and prepares data for the home page.
 */
export async function loader(args: Route.LoaderArgs): Promise<HomePageData> {
    const logger = getLogger(args.context);
    logger.debug('HomePage: loader starting');

    const config = getConfig(args.context);
    const requestUrl = new URL(args.request.url);

    // Redirect bare "/" to the default site/locale prefixed homepage
    if (requestUrl.pathname === '/' && config.url?.prefix && config.url.prefix !== '/') {
        const siteRef = config.siteAliasMap?.[config.defaultSiteId] ?? config.defaultSiteId;
        const defaultSite = config.commerce.sites.find((s) => s.id === config.defaultSiteId);
        const defaultLocale = defaultSite?.defaultLocale ?? resolveFallbackLanguage(config.i18n.fallbackLng);
        const localeRef = config.localeAliasMap?.[defaultLocale] ?? defaultLocale;
        const prefixedPath = resolvePrefix({
            prefix: config.url.prefix,
            params: { siteId: siteRef, localeId: localeRef },
        });
        throw redirect(`${prefixedPath}/`);
    }

    const pageUrl = buildCanonicalUrl(requestUrl.origin, requestUrl.pathname, requestUrl.search);
    const wishlistInitialState = fetchWishlistInitialState(args.context);
    const ogImageUrl = new URL(hero01, requestUrl.origin).href;

    const isPageDesignerActive = isDesignModeActive(args.request) || isPreviewModeActive(args.request);

    if (isPageDesignerActive) {
        const pageId = (requestUrl.searchParams.get('pageId') ?? HOMEPAGE_ROW_IDS[0]) as HomepageRowId;
        const row = await fetchHomepageRow(args, pageId);
        return {
            rows: [row],
            wishlistInitialState,
            pageUrl,
            ogImageUrl,
        };
    }

    const rows = await Promise.all(HOMEPAGE_ROW_IDS.map((id) => fetchHomepageRow(args, id)));

    return {
        rows,
        wishlistInitialState,
        pageUrl,
        ogImageUrl,
    };
}

function hasMainRegionContent(page: PageWithComponentData | null): boolean {
    if (!page) {
        return false;
    }

    const mainRegion = page.regions?.find((region) => region.id === 'main');
    return (mainRegion?.components?.length ?? 0) > 0;
}

/** Primary region for Page Designer preview — must match a region on the edited page. */
export function getPrimaryRegionId(page: PageWithComponentData | null): string {
    const regionIds = page?.regions?.map((region) => region.id) ?? [];
    if (regionIds.includes('main')) {
        return 'main';
    }
    if (regionIds.includes('headerbanner')) {
        return 'headerbanner';
    }
    return regionIds[0] ?? 'main';
}

function HomepageRowSection({ row, isDesignMode }: { row: HomePageRow; isDesignMode: boolean }): ReactElement | null {
    // Runtime: hide rows with no content (404, unscheduled, or empty main region).
    if (!isDesignMode && !hasMainRegionContent(row.page)) {
        return null;
    }

    const regionId = isDesignMode ? getPrimaryRegionId(row.page) : 'main';
    const rowNumber = row.id.replace('homepage-row-', '');

    return (
        <section className={row.id.startsWith('homepage-row-') ? `r-homepage-row-${rowNumber}` : undefined}>
            {/*
              Always mount Region in Page Designer so PageDesignerPageMetadataProvider can handshake.
              Region renders an empty drop zone in design mode even when the region has no components.
            */}
            <Region page={row.page} regionId={regionId} />
        </section>
    );
}

/**
 * Home page composed of up to seven Page Designer rows (homepage-row-1 … homepage-row-7).
 */
export default function HomePage({ loaderData }: { loaderData: HomePageData }) {
    const { t } = useTranslation('home');
    const { isDesignMode, isPreviewMode } = usePageDesignerMode();
    const isPageDesignerActive = isDesignMode || isPreviewMode;

    return (
        <WishlistProvider initialState={loaderData.wishlistInitialState}>
            <div className="r-homepage-row-container r-ups-automargin -mt-8">
                <SeoMeta
                    rawTitle
                    title={t('meta.title', { defaultValue: 'NextGen PWA Kit Store' })}
                    description={t('meta.description', {
                        defaultValue: 'Welcome to our web store for high performers!',
                    })}
                    openGraph={{
                        type: 'website',
                        url: loaderData.pageUrl,
                        image: loaderData.ogImageUrl,
                    }}
                />
                {loaderData.rows.map((row) => (
                    <HomepageRowSection key={row.id} row={row} isDesignMode={isPageDesignerActive} />
                ))}
            </div>
        </WishlistProvider>
    );
}
